import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

// опционально читаем настройки (mute/volume), если есть твой модуль notifications
let getNotify = () => ({ enabled: true, volume: 0.6 });
try {
    const mod = require("../../utils/notifications");
    if (mod?.notify?.get) getNotify = mod.notify.get;
} catch { }

export default function RingtoneManager() {
    const { status, incoming } = useSelector((s) => s.call); // 'ringing' | 'dialing' | 'connecting' | 'in-call' | 'idle'
    const ctxRef = useRef(null);
    const oscRef = useRef(null);
    const gainRef = useRef(null);
    const loopTimerRef = useRef(0);
    const runningRef = useRef(false);

    const ensureCtx = () => {
        if (ctxRef.current) return ctxRef.current;
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return null;
        ctxRef.current = new AC();
        return ctxRef.current;
    };

    const stop = () => {
        runningRef.current = false;
        if (loopTimerRef.current) { clearTimeout(loopTimerRef.current); loopTimerRef.current = 0; }
        const ctx = ctxRef.current;
        const g = gainRef.current;
        const o = oscRef.current;
        try {
            if (ctx && g) {
                const t = ctx.currentTime;
                g.gain.cancelScheduledValues(t);
                g.gain.setValueAtTime(g.gain.value || 0.0001, t);
                g.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
            }
        } catch { }
        try { o && o.stop(); } catch { }
        oscRef.current = null;
        gainRef.current = null;
    };

    const start = (mode /* 'incoming' | 'outgoing' */) => {
        const { enabled, volume } = getNotify();
        if (!enabled) return;
        const ctx = ensureCtx();
        if (!ctx || ctx.state === "suspended") return; // звук не разблокирован — просто молчим, НИЧЕГО не сбрасываем

        stop(); // чистим, если что-то играло

        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = "sine";

        // паттерны
        // incoming: 950 Гц, 0.16 c сигнала, пауза до 0.7 c, повтор
        // outgoing: 440/480 Гц перестройка, 1 c сигнала, 2 c пауза
        let f1 = 950, f2 = 0, onMs = 160, offMs = 540; // итого ~700мс цикл
        if (mode === "outgoing") { f1 = 440; f2 = 480; onMs = 1000; offMs = 2000; }

        const vol = Math.max(0, Math.min(1, Number(volume ?? 0.6))) * 0.12;

        const tick = () => {
            if (!runningRef.current) return;
            const now = ctx.currentTime;

            // включаем громкость (атака)
            try {
                gain.gain.cancelScheduledValues(now);
                gain.gain.setValueAtTime(gain.gain.value || 0.0001, now);
                gain.gain.linearRampToValueAtTime(vol, now + 0.02);
            } catch { }

            // исходящий: имитация ringback — быстрая смена 440/480
            if (f2 > 0) {
                let parts = Math.max(1, Math.floor(onMs / 30));
                for (let i = 0; i < parts; i++) {
                    osc.frequency.setValueAtTime(i % 2 === 0 ? f1 : f2, now + i * 0.03);
                }
            } else {
                osc.frequency.setValueAtTime(f1, now);
            }

            // релиз
            try { gain.gain.exponentialRampToValueAtTime(0.0001, now + onMs / 1000 - 0.02); } catch { }

            // следующий импульс
            loopTimerRef.current = setTimeout(tick, onMs + offMs);
        };

        try { osc.start(); } catch { }
        runningRef.current = true;
        oscRef.current = osc;
        gainRef.current = gain;

        loopTimerRef.current = setTimeout(tick, 50);
    };

    useEffect(() => {
        if (status === "ringing") {
            start(incoming ? "incoming" : "outgoing");
        } else {
            stop();
        }
        return () => stop();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, incoming]);

    useEffect(() => () => stop(), []);

    return null;
}
